import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GetTenantGroupDto, TenantGroupDto } from '@flexyoo/api/models';
import { TenantApiService } from '@flexyoo/api/services';
import { AuthenticationService } from '@flexyoo/auth';
import { PopUpService } from '@flexyoo/shared/ui';
import {
  TenantGroupHomeDataAccessService,
  TenantGroupSettingsService,
} from '@flexyoo/tenant-group/data-access';
import { TenantGroupSettings } from '@flexyoo/tenant-group/models';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, of, switchMap, take } from 'rxjs';
import { AdminClientsSelectUserDialogService } from '../../../clients/components/admin-clients-list/admin-clients-select-user-dialog/admin-clients-select-user-dialog.service';
import { TenantGroupFormDialogAction } from '../../declarations/types/tenant-group-form-dialog-action.type';
import { TenantGroupFormDialogComponent } from '../tenant-group-form-dialog/tenant-group-form-dialog.component';
import { TenantGroupsTableDataSourceService } from '../tenant-groups-table/tenant-groups-table-data-source.service';
import { TenantGroupsTableComponent } from '../tenant-groups-table/tenant-groups-table.component';

@Component({
  selector: 'app-tenant-groups',
  templateUrl: './tenant-groups.component.html',
  styleUrl: './tenant-groups.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TenantGroupsTableComponent],
  providers: [TenantGroupsTableDataSourceService],
})
export class TenantGroupsComponent {
  private readonly dialog = inject(MatDialog);
  private readonly tenantGroupHomeService = inject(TenantGroupHomeDataAccessService);
  private readonly tenantGroupSettingsService = inject(TenantGroupSettingsService);
  private readonly translateService = inject(TranslateService);
  private readonly popUpService = inject(PopUpService);
  private readonly authService = inject(AuthenticationService);
  private readonly tenantApi = inject(TenantApiService);
  private readonly selectUserDialogService = inject(AdminClientsSelectUserDialogService);
  protected readonly dataSource = inject(TenantGroupsTableDataSourceService);

  private dialogRef: MatDialogRef<TenantGroupFormDialogComponent>;

  protected onItemAction({
    action,
    item,
  }: {
    action: TenantGroupFormDialogAction;
    item: TenantGroupDto;
  }): void {
    switch (action) {
      case 'impersonate':
        this.impersonate(item);
        break;
      case 'create':
        this.openBudgetTypeFormDialog({ action: 'create', item: null });
        break;
      case 'edit':
        this.openBudgetTypeFormDialog({ action, item });
        break;
      case 'delete':
        this.deleteTenantGroup(item.Id, item.Name ?? '');
        break;
    }
  }

  private deleteTenantGroup(id: number, name: string): void {
    this.popUpService
      .openConfirmDialog({
        title: 'admin.tenant-groups.delete-confirmation.title',
        text: this.translateService.instant('admin.tenant-groups.delete-confirmation.description', {
          name,
        }),
      })
      .pipe(
        filter((result) => result),
        switchMap(() => this.tenantGroupHomeService.deleteTenantGroup(id))
      )
      .subscribe(() => this.dataSource.reloadData());
  }

  private openBudgetTypeFormDialog({
    action,
    item,
  }: {
    action: TenantGroupFormDialogAction;
    item: TenantGroupDto;
  }): void {
    const details$ = item?.Id
      ? combineLatest([
          this.tenantGroupHomeService.getTenantGroupDetails(item.Id),
          this.tenantGroupSettingsService.updateAndLoadTenantGroupSettings(item.Id, true),
          this.tenantGroupHomeService.hasChoicePeriodStarted(item.Id),
        ])
      : of(null);

    this.dataSource.loadingState$.next('loading');

    details$.pipe(take(1)).subscribe((detailsInfo) => {
      const details = detailsInfo?.[0];
      const settings = detailsInfo?.[1];
      const isFirstChoicePeriodStarted = detailsInfo?.[2] ?? false;
      this.dataSource.loadingState$.next('loaded');
      this.dialogRef = this.dialog.open<
        TenantGroupFormDialogComponent,
        {
          action: TenantGroupFormDialogAction;
          item: GetTenantGroupDto;
          settings: TenantGroupSettings;
          isFirstChoicePeriodStarted: boolean;
        },
        boolean
      >(TenantGroupFormDialogComponent, {
        disableClose: true,
        width: '650px',
        data: {
          action,
          item: details,
          settings,
          isFirstChoicePeriodStarted,
        },
      });

      this.dialogRef
        .afterClosed()
        .pipe(
          take(1),
          filter((res) => !!res)
        )
        .subscribe(() => this.dataSource.reloadData());
    });
  }

  private impersonate(item: TenantGroupDto): void {
    this.tenantApi.get(item.OwnerTenantId).subscribe((tenant) => {
      const users = tenant.Users ?? [];
      if (users.length === 0) {
        return;
      } else if (users.length === 1) {
        this.authService.impersonateAsTenant(users[0].UserId, tenant.Id);
      } else {
        this.selectUserDialogService
          .open({
            users,
          })
          .then((userId) => {
            if (userId) {
              this.authService.impersonateAsTenant(userId, tenant.Id);
            }
          });
      }
    });
  }
}
